import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  useEditExerciseMutation,
  useGetExerciseByIdQuery,
  useUploadThumbnailMutation,
  useUploadVideoMutation,
  useGetEquipmentQuery,
  useGetMusclesQuery,
} from "../../../services";
import { ApiEndpoints } from "../../../core";

const ExerciseEdit: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { exerciseid } = useParams();

  const { data: equipmentData } = useGetEquipmentQuery({ limit: -1 });
  const { data: muscleData } = useGetMusclesQuery({ limit: -1 });
  const {
    data: exerciseData,
    isLoading: isLoadingExercise,
    isError: isErrorExercise,
  } = useGetExerciseByIdQuery(exerciseid!, {
    skip: !exerciseid,
  });

  const [editExercise, { isLoading: isUpdating }] = useEditExerciseMutation();

  const [form, setForm] = useState({
    name: "",
    muscle: "",
    equipment: "",
  });

  // Hold upload responses (e.g., URL/ID) if your API returns them
  const [thumbUploadResponse, setThumbUploadResponse] = useState<any>(null);
  const [videoUploadResponse, setVideoUploadResponse] = useState<any>(null);

  const [existingThumbUrl, setExistingThumbUrl] = useState<string>("");
  const [existingVideoUrl, setExistingVideoUrl] = useState<string>("");

  // Populate form when exercise data is loaded
  React.useEffect(() => {
    if (exerciseData) {
      const exercise =
        exerciseData?.result ?? exerciseData?.results ?? exerciseData;

      setForm({
        name: exercise?.name || "",
        muscle: exercise?.muscles?.[0]?._id || "", // use id
        equipment: exercise?.equipment?._id || "", // use id
      });

      // Set existing thumbnail and video if available
      if (exercise?.thumbnail) {
        if (typeof exercise.thumbnail === "string") {
          // If it's just an ID
          setThumbUploadResponse(exercise.thumbnail);
        } else if (exercise.thumbnail._id) {
          // If it's an object with _id and key
          setThumbUploadResponse(exercise.thumbnail._id);

          // Construct thumbnail preview URL
          const thumbBaseUrl =
            exercise?.type === "Custom"
              ? ApiEndpoints.customWorkoutThumbnailURL
              : ApiEndpoints.defaultWorkoutThumbnailURL;

          const thumbnailUrl = exercise.thumbnail.key
            ? `${thumbBaseUrl}/${exercise.thumbnail.key}`
            : "";
          setExistingThumbUrl(thumbnailUrl);
        }
      }

      if (exercise?.video) {
        if (typeof exercise.video === "string") {
          // If it's just an ID
          setVideoUploadResponse(exercise.video);
        } else if (exercise.video._id) {
          // If it's an object with _id and key
          setVideoUploadResponse(exercise.video._id);

          // Construct video preview URL
          const videoBaseUrl =
            exercise?.type === "Custom"
              ? ApiEndpoints.originalCustomWorkoutURL
              : ApiEndpoints.originalDefaultWorkoutURL;

          const videoUrl = exercise.video.key
            ? `${videoBaseUrl}/${exercise.video.key}`
            : "";
          setExistingVideoUrl(videoUrl);
        }
      }
    }
  }, [exerciseData]);

  const cardClass =
    theme === "dark"
      ? "bg-[#0F0F0F] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const inputClassBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputClassBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24]`
      : `${inputClassBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24]`;

  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const [uploadThumbnail] = useUploadThumbnailMutation();
  const [uploadVideo] = useUploadVideoMutation();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.muscle || !form.equipment) {
      console.warn("Validation failed - missing fields", { form });
      return;
    }
    const payload = {
      name: form.name.trim(),
      muscles: [form.muscle], // send muscle id
      equipment: form.equipment, // send equipment id
      thumbnail:
        thumbUploadResponse ??
        existingThumbUrl ??
        "https://gymstream-dev.blr1.cdn.digitaloceanspaces.com/files/subscriber/workouts/thumbnail/b5208ae3-425e-4e77-89c5-3759dcd055bc.png",
      video: videoUploadResponse ?? existingVideoUrl,
    };

    try {
      const resp = await editExercise({
        id: exerciseid!,
        ...payload,
      }).unwrap();

      navigate(adminRoutes.exerciseList);
    } catch (e) {
      console.error("Edit exercise failed:", e);
    }
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  // New state for files + previews + progress
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState<string>("");
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>("");
  const [thumbProgress, setThumbProgress] = useState<number>(0);
  const [videoProgress, setVideoProgress] = useState<number>(0);
  const [thumbUploading, setThumbUploading] = useState<boolean>(false);
  const [videoUploading, setVideoUploading] = useState<boolean>(false);

  const formatBytes = (n: number) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n}B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(0)}kb`;
    return `${(kb / 1024).toFixed(1)}MB`;
  };

  // New drag-over flags
  const [isThumbDrag, setIsThumbDrag] = useState<boolean>(false);
  const [isVideoDrag, setIsVideoDrag] = useState<boolean>(false);

  // Frame selection modal state
  const [showFrameSelector, setShowFrameSelector] = useState<boolean>(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [frameThumbnails, setFrameThumbnails] = useState<string[]>([]);
  const [selectedFrameIndex, setSelectedFrameIndex] = useState<number>(0);
  const [frameError, setFrameError] = useState<string>("");

  // Video preview modal state
  const [showVideoPreview, setShowVideoPreview] = useState<boolean>(false);

  // Thumbnail preview modal state
  const [showThumbPreview, setShowThumbPreview] = useState<boolean>(false);

  const uploadFile = async (f: File, kind: "thumbnail" | "video") => {
    if (!f) return;
    if (kind === "thumbnail") {
      setThumbnailFile(f);
      setThumbPreviewUrl(URL.createObjectURL(f));
      setThumbUploading(true);
      setThumbProgress(0);
      setThumbUploadResponse(null);
      setExistingThumbUrl(""); // Clear existing thumbnail when uploading new one
    } else {
      setVideoFile(f);
      setVideoPreviewUrl(URL.createObjectURL(f));
      setVideoUploading(true);
      setVideoProgress(0);
      setVideoUploadResponse(null);
      setExistingVideoUrl(""); // Clear existing video when uploading new one
    }

    try {
      const uploadMutation =
        kind === "thumbnail" ? uploadThumbnail : uploadVideo;

      const res = await uploadMutation({
        file: f,
        size: f.size,
        onUploadProgress: (percent: number) => {
          if (kind === "thumbnail") setThumbProgress(percent);
          else setVideoProgress(percent);
        },
      }).unwrap();
      const returnedId = res?.result?._id ?? res?._id ?? null;

      if (kind === "thumbnail") {
        setThumbUploadResponse(returnedId);
      } else {
        setVideoUploadResponse(returnedId);
        // Only try frame selection for formats the browser can decode
        const canExtract = f.type === "video/mp4" || f.type === "video/webm";
        if (
          !thumbnailFile &&
          !thumbUploadResponse &&
          !existingThumbUrl &&
          canExtract
        ) {
          setFrameError("");
          setSelectedFrameIndex(0);
          setFrameThumbnails([]);
          setShowFrameSelector(true);
        } else if (!canExtract) {
          setFrameError(
            "Frame selection is only supported for MP4/WebM in browser.",
          );
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`${kind} upload failed:`, err);
      if (kind === "thumbnail") setThumbUploadResponse(null);
      else setVideoUploadResponse(null);
    } finally {
      if (kind === "thumbnail") {
        setThumbUploading(false);
        setThumbProgress((p) => (p >= 100 ? 100 : p));
      } else {
        setVideoUploading(false);
        setVideoProgress((p) => (p >= 100 ? 100 : p));
      }
    }
  };

  const handleThumbnailChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      const f = e.target.files[0];
      await uploadFile(f, "thumbnail");
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const f = e.target.files[0];
      await uploadFile(f, "video");
    }
  };

  const onThumbDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsThumbDrag(true);
  };
  const onThumbDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsThumbDrag(false);
  };
  const onThumbDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsThumbDrag(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const f = dt.files[0];
      await uploadFile(f, "thumbnail");
    }
  };

  const onVideoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVideoDrag(true);
  };
  const onVideoDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVideoDrag(false);
  };
  const onVideoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsVideoDrag(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const f = dt.files[0];
      await uploadFile(f, "video");
    }
  };

  // Track blob URLs with refs for proper cleanup
  const thumbBlobRef = React.useRef<string>("");
  const videoBlobRef = React.useRef<string>("");

  // Cleanup old blob URL when creating new one
  React.useEffect(() => {
    if (
      thumbPreviewUrl &&
      thumbBlobRef.current &&
      thumbBlobRef.current !== thumbPreviewUrl
    ) {
      URL.revokeObjectURL(thumbBlobRef.current);
    }
    thumbBlobRef.current = thumbPreviewUrl;
  }, [thumbPreviewUrl]);

  React.useEffect(() => {
    if (
      videoPreviewUrl &&
      videoBlobRef.current &&
      videoBlobRef.current !== videoPreviewUrl
    ) {
      URL.revokeObjectURL(videoBlobRef.current);
    }
    videoBlobRef.current = videoPreviewUrl;
  }, [videoPreviewUrl]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (thumbBlobRef.current) URL.revokeObjectURL(thumbBlobRef.current);
      if (videoBlobRef.current) URL.revokeObjectURL(videoBlobRef.current);
    };
  }, []);

  const removeThumb = () => {
    if (thumbPreviewUrl) URL.revokeObjectURL(thumbPreviewUrl);
    setThumbnailFile(null);
    setThumbPreviewUrl("");
    setThumbProgress(0);
    setThumbUploading(false);
    setThumbUploadResponse(null);
    setExistingThumbUrl(""); // Clear existing thumbnail
  };
  const removeVideo = () => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoFile(null);
    setVideoPreviewUrl("");
    setVideoProgress(0);
    setVideoUploading(false);
    setVideoUploadResponse(null);
    setExistingVideoUrl(""); // Clear existing video
  };

  // Extract frame thumbnails when modal opens
  React.useEffect(() => {
    if (
      !(
        showFrameSelector &&
        (videoPreviewUrl || existingVideoUrl) &&
        videoRef.current
      )
    )
      return;

    const video = videoRef.current;
    setFrameThumbnails([]); /* ensure reset */
    setSelectedFrameIndex(0);

    // Prepare video element to load metadata reliably
    video.preload = "metadata";
    video.muted = true;
    // @ts-ignore
    video.playsInline = true;
    try {
      video.crossOrigin = "anonymous";
    } catch {
      /* ignore */
    }

    let isCancelled = false;
    const tempCanvas = document.createElement("canvas");
    const ctx = tempCanvas.getContext("2d");
    if (!ctx) return;

    const clean = () => {
      video.onseeked = null;
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("error", onErr);
    };

    const onErr = () => {
      if (isCancelled) return;
      setFrameError("Unable to read video metadata. Try MP4/WebM.");
      clean();
    };

    const onMeta = () => {
      if (isCancelled) return;
      const duration = video.duration;
      if (!isFinite(duration) || duration <= 0) {
        setFrameError("Invalid duration. Try MP4/WebM.");
        clean();
        return;
      }

      const frameCount = 10;
      const interval = duration / frameCount;
      const frames: string[] = [];
      let currentIndex = 0;

      const captureFrame = () => {
        if (isCancelled) return;
        if (currentIndex >= frameCount) {
          setFrameThumbnails(frames);
          clean();
          return;
        }
        // Avoid seeking beyond duration
        const time = Math.min(duration - 0.05, currentIndex * interval);
        video.currentTime = time < 0 ? 0 : time;
      };

      video.onseeked = () => {
        if (isCancelled) return;
        tempCanvas.width = video.videoWidth || 1280;
        tempCanvas.height = video.videoHeight || 720;
        // Ensure frame is ready
        requestAnimationFrame(() => {
          try {
            ctx.drawImage(video, 0, 0, tempCanvas.width, tempCanvas.height);
            frames.push(tempCanvas.toDataURL("image/jpeg", 0.7));
          } catch {
            // draw may fail if cross-origin; just skip
          }
          currentIndex++;
          captureFrame();
        });
      };

      captureFrame();
    };

    video.addEventListener("loadedmetadata", onMeta, { once: true });
    video.addEventListener("error", onErr, { once: true });

    // Kick metadata loading explicitly
    try {
      video.load();
    } catch {
      /* ignore */
    }

    return () => {
      isCancelled = true;
      clean();
    };
  }, [showFrameSelector, videoPreviewUrl, existingVideoUrl]);

  const captureSelectedFrame = async () => {
    // use the selected frame from the filmstrip
    const frameDataUrl = frameThumbnails[selectedFrameIndex];
    if (!frameDataUrl) return;

    // convert data URL to blob
    const res = await fetch(frameDataUrl);
    const blob = await res.blob();
    const file = new File([blob], "thumbnail.jpg", { type: "image/jpeg" });

    // upload the captured frame as thumbnail
    setThumbnailFile(file);
    setThumbPreviewUrl(URL.createObjectURL(file));
    setThumbUploading(true);
    setThumbProgress(0);
    setThumbUploadResponse(null);

    try {
      const uploadRes = await uploadThumbnail({
        file,
        size: file.size,
        onUploadProgress: (percent: number) => setThumbProgress(percent),
      }).unwrap();
      const returnedId = uploadRes?.result?._id ?? uploadRes?._id ?? null;
      setThumbUploadResponse(returnedId);
    } catch (err) {
      console.error("Thumbnail upload failed:", err);
      setThumbUploadResponse(null);
    } finally {
      setThumbUploading(false);
      setThumbProgress((p) => (p >= 100 ? 100 : p));
    }

    // close modal
    setShowFrameSelector(false);
  };

  // Show loading state while fetching exercise data
  if (isLoadingExercise) {
    return (
      <div
        className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen flex items-center justify-center ${
          theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      >
        <div className="text-center">
          <div className="text-lg">Loading exercise data...</div>
        </div>
      </div>
    );
  }

  // Show error state if exercise not found
  if (isErrorExercise) {
    return (
      <div
        className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen flex items-center justify-center ${
          theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
        }`}
      >
        <div className="text-center">
          <div className="text-lg text-red-500">Error loading exercise</div>
          <Link
            to={adminRoutes.exerciseList}
            className="mt-4 inline-block text-[#B6CB24] underline"
          >
            Back to exercises
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${
        theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50"
      }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Breadcrumb + Title */}
      <div className="mb-4">
        <h1
          className={`text-xl font-semibold ${
            theme === "dark" ? "text-gray-100" : "text-gray-900"
          }`}
        >
          Exercise Overview
        </h1>
        <div className="text-sm mt-1">
          <Link
            to={`${adminRoutes.exerciseList}`}
            className={theme === "dark" ? "text-gray-400" : "text-gray-500"}
          >
            All Exercises
          </Link>
          <span
            className={theme === "dark" ? "text-gray-600" : "text-gray-400"}
          >
            {" "}
            &nbsp;&gt;&nbsp;{" "}
          </span>
          <span className="text-[#B6CB24] font-medium">Edit Exercise</span>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-4 md:p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            Edit Exercise
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Exercise Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Muscle Group
            </label>
            <select
              name="muscle"
              value={form.muscle}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Enter here</option>
              {muscleData?.results.map((m: any) => (
                <option key={m._id} value={m._id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Equipment
            </label>
            <select
              name="equipment"
              value={form.equipment}
              onChange={handleChange}
              className={inputClass}
            >
              <option value="">Enter here</option>
              {equipmentData?.results.map((e: any) => (
                <option key={e._id} value={e._id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Video */}
          <div>
            <label
              className={`block text-sm mb-1 ${labelClass} flex items-center justify-between`}
            >
              <span>Exercise Video</span>
              <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                  theme === "dark"
                    ? "bg-[#232323] text-[#B6CB24]"
                    : "bg-gray-100 text-gray-700"
                }`}
                title="Max 30MB. MP4/AVI"
              >
                i
              </span>
            </label>
            <div className="text-xs text-gray-400 mb-2">
              Max file size is 30mb. Supported files are .mp4 and .avi
            </div>

            {!videoFile && !existingVideoUrl ? (
              <div
                onClick={() => document.getElementById("videoInput")?.click()}
                onDragOver={onVideoDragOver}
                onDragEnter={onVideoDragOver}
                onDragLeave={onVideoDragLeave}
                onDrop={onVideoDrop}
                className={`border-dashed border-2 rounded-md p-8 text-center cursor-pointer ${
                  theme === "dark"
                    ? "border-[#C6D92C] bg-[#111111]"
                    : "border-gray-300 bg-gray-50"
                } ${isVideoDrag ? "ring-2 ring-[#C6D92C]/60" : ""}`}
              >
                <input
                  id="videoInput"
                  type="file"
                  accept=".mp4,.avi"
                  onChange={handleVideoChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center">
                  {/* ...existing SVG... */}
                  <svg
                    className="w-8 h-8 mb-2 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Drag and drop file here
                  </p>
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-500" : "text-gray-500"
                    } my-1`}
                  >
                    or
                  </p>
                  <p
                    className={`text-sm underline ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Click to browse files
                  </p>
                </div>
              </div>
            ) : (
              <div
                className={`rounded-md px-3 py-2 flex items-center justify-between ${
                  theme === "dark"
                    ? "bg-[#111111] border border-[#2C2C2C] text-gray-200"
                    : "bg-gray-50 border border-gray-300 text-gray-800"
                }`}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="5"
                      width="14"
                      height="14"
                      rx="2"
                      stroke={theme === "dark" ? "#C6D92C" : "#4B5563"}
                      strokeWidth="1.5"
                    />
                    <path
                      d="M17 9l4-2v10l-4-2V9z"
                      stroke={theme === "dark" ? "#C6D92C" : "#4B5563"}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>

                  <span className="truncate text-sm">
                    {videoFile?.name || "Existing video"}
                  </span>

                  {(videoPreviewUrl || existingVideoUrl) && !videoUploading && (
                    <button
                      type="button"
                      onClick={() => {
                        // Recreate blob URL if it doesn't exist
                        if (videoFile && !videoPreviewUrl) {
                          setVideoPreviewUrl(URL.createObjectURL(videoFile));
                        }
                        setShowVideoPreview(true);
                      }}
                      className="text-xs underline ml-2"
                      style={{
                        color: theme === "dark" ? "#C6D92C" : "#4B5563",
                      }}
                    >
                      Preview
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {videoFile && (
                    <span className="text-xs opacity-70">
                      {formatBytes(videoFile.size)}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={removeVideo}
                    aria-label="Remove video"
                    className="p-1 rounded hover:opacity-80"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M15 9L9 14.9996M15 15L9 9.00039"
                        stroke={theme === "dark" ? "#F3F3F6" : "#374151"}
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {videoFile && videoUploading && (
              <div className="mt-2">
                <div
                  className={`h-2 rounded ${
                    theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-200"
                  }`}
                >
                  <div
                    className="h-2 rounded bg-[#C6D92C] transition-all"
                    style={{ width: `${videoProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail */}
          <div>
            <label
              className={`block text-sm mb-1 ${labelClass} flex items-center justify-between`}
            >
              <span>Exercise Thumbnail</span>
              <span
                className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                  theme === "dark"
                    ? "bg-[#232323] text-[#B6CB24]"
                    : "bg-gray-100 text-gray-700"
                }`}
                title="Max 5MB. JPEG/PNG"
              >
                i
              </span>
            </label>
            <div className="text-xs text-gray-400 mb-2">
              Max file size is 5mb. Supported files are JPEG and PNG
            </div>

            {!thumbnailFile && !existingThumbUrl ? (
              <div
                onClick={() =>
                  document.getElementById("thumbnailInput")?.click()
                }
                onDragOver={onThumbDragOver}
                onDragEnter={onThumbDragOver}
                onDragLeave={onThumbDragLeave}
                onDrop={onThumbDrop}
                className={`border-dashed border-2 rounded-md p-8 text-center cursor-pointer ${
                  theme === "dark"
                    ? "border-[#C6D92C] bg-[#111111]"
                    : "border-gray-300 bg-gray-50"
                } ${isThumbDrag ? "ring-2 ring-[#C6D92C]/60" : ""}`}
              >
                <input
                  id="thumbnailInput"
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center">
                  {/* ...existing SVG... */}
                  <svg
                    className="w-8 h-8 mb-2 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Drag and drop file here
                  </p>
                  <p
                    className={`text-sm ${
                      theme === "dark" ? "text-gray-500" : "text-gray-500"
                    } my-1`}
                  >
                    or
                  </p>
                  <p
                    className={`text-sm underline ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Click to browse files
                  </p>
                </div>
              </div>
            ) : (
              <div
                onDragOver={onThumbDragOver}
                onDragEnter={onThumbDragOver}
                onDragLeave={onThumbDragLeave}
                onDrop={onThumbDrop}
                className={`rounded-md px-3 py-2 flex items-center justify-between ${
                  theme === "dark"
                    ? "bg-[#111111] border border-[#2C2C2C] text-gray-200"
                    : "bg-gray-50 border border-gray-300 text-gray-800"
                }`}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                      stroke={theme === "dark" ? "#C6D92C" : "#4B5563"}
                      strokeWidth="1.5"
                    />
                    <path
                      d="M14 3v5h5"
                      stroke={theme === "dark" ? "#C6D92C" : "#4B5563"}
                      strokeWidth="1.5"
                    />
                  </svg>
                  <span className="truncate text-sm">
                    {thumbnailFile?.name || "Existing thumbnail"}
                    {thumbnailFile && (
                      <span className="ml-2 text-xs opacity-70">
                        {formatBytes(thumbnailFile.size)}
                      </span>
                    )}
                  </span>

                  {(thumbPreviewUrl || existingThumbUrl) && !thumbUploading && (
                    <button
                      type="button"
                      onClick={() => setShowThumbPreview(true)}
                      className="text-xs underline ml-2"
                      style={{
                        color: theme === "dark" ? "#C6D92C" : "#4B5563",
                      }}
                    >
                      Preview
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={removeThumb}
                  aria-label="Remove thumbnail"
                  className="p-1 rounded hover:opacity-80"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 9L9 14.9996M15 15L9 9.00039"
                      stroke={theme === "dark" ? "#F3F3F6" : "#374151"}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}

            {thumbnailFile && thumbUploading && (
              <div className="mt-2">
                <div
                  className={`h-2 rounded ${
                    theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-200"
                  }`}
                >
                  <div
                    className="h-2 rounded bg-[#C6D92C] transition-all"
                    style={{ width: `${thumbProgress}%` }}
                  />
                </div>
                <div className="text-xs mt-1 opacity-80">Uploading...</div>
              </div>
            )}

            {/* "OR" divider and Select from Video button */}
            {(videoFile || existingVideoUrl) &&
              (videoUploadResponse || existingVideoUrl) &&
              !thumbUploading && (
                <>
                  <div className="flex items-center gap-3 my-3">
                    <div
                      className={`flex-1 h-px ${
                        theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-300"
                      }`}
                    />
                    <span
                      className={`text-xs font-medium ${
                        theme === "dark" ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      OR
                    </span>
                    <div
                      className={`flex-1 h-px ${
                        theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-300"
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Recreate blob URL if it doesn't exist
                      if (videoFile && !videoPreviewUrl) {
                        setVideoPreviewUrl(URL.createObjectURL(videoFile));
                      }
                      setFrameError("");
                      setSelectedFrameIndex(0);
                      setFrameThumbnails([]);
                      setShowFrameSelector(true);
                    }}
                    className={`w-full py-2.5 px-4 rounded-md border-2 border-dashed font-medium text-sm transition-all ${
                      theme === "dark"
                        ? "border-[#C6D92C] bg-[#111111] text-[#C6D92C] hover:bg-[#1a1a1a]"
                        : "border-[#B6CB24] bg-gray-50 text-[#7a8f1a] hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <rect
                          x="3"
                          y="5"
                          width="14"
                          height="14"
                          rx="2"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M17 9l4-2v10l-4-2V9z"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>Select Frame from Video</span>
                    </div>
                  </button>
                </>
              )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={isUpdating}
            className="px-8 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
            }}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
          <Link
            to={`${adminRoutes.exerciseList}`}
            className={`px-5 py-1.5 rounded-md font-medium ${
              theme === "dark"
                ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            Cancel
          </Link>
        </div>
      </form>

      {/* Video Preview Modal */}
      {showVideoPreview && (videoPreviewUrl || existingVideoUrl) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={`rounded-lg p-6 max-w-4xl w-full mx-4 ${
              theme === "dark"
                ? "bg-[#1E1E1E] border border-[#2C2C2C]"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Video Preview
              </h3>
              <button
                type="button"
                onClick={() => setShowVideoPreview(false)}
                className={`p-1 rounded hover:opacity-80 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
                aria-label="Close preview"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                src={videoPreviewUrl || existingVideoUrl}
                controls
                className="w-full max-h-[70vh]"
                style={{ objectFit: "contain" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowVideoPreview(false)}
                className={`px-6 py-2 rounded-lg font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thumbnail Preview Modal */}
      {showThumbPreview && (thumbPreviewUrl || existingThumbUrl) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={`rounded-lg p-6 max-w-4xl w-full mx-4 ${
              theme === "dark"
                ? "bg-[#1E1E1E] border border-[#2C2C2C]"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                Thumbnail Preview
              </h3>
              <button
                type="button"
                onClick={() => setShowThumbPreview(false)}
                className={`p-1 rounded hover:opacity-80 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
                aria-label="Close preview"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="relative bg-black rounded-lg overflow-hidden flex justify-center">
              <img
                src={thumbPreviewUrl || existingThumbUrl}
                alt="Thumbnail Preview"
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowThumbPreview(false)}
                className={`px-6 py-2 rounded-lg font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Frame Selector Modal */}
      {showFrameSelector && (videoPreviewUrl || existingVideoUrl) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div
            className={`rounded-lg p-6 max-w-4xl w-full mx-4 ${
              theme === "dark"
                ? "bg-[#1E1E1E] border border-[#2C2C2C]"
                : "bg-white border border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}
            >
              Select Thumbnail Frame
            </h3>

            {/* Main video preview (hidden but used for frame extraction) */}
            <video
              ref={videoRef}
              src={videoPreviewUrl || existingVideoUrl}
              className="hidden"
            />

            {/* Selected frame preview */}
            <div className="mb-4 relative">
              {frameThumbnails.length > 0 ? (
                <img
                  src={frameThumbnails[selectedFrameIndex]}
                  alt="Selected frame"
                  className="w-full rounded"
                  style={{ maxHeight: "400px", objectFit: "contain" }}
                />
              ) : (
                <div
                  className={`w-full rounded flex items-center justify-center ${
                    theme === "dark" ? "bg-[#111111]" : "bg-gray-100"
                  }`}
                  style={{ height: "400px" }}
                >
                  <span className="text-gray-500">Loading frames...</span>
                </div>
              )}
            </div>

            {/* Filmstrip of frame thumbnails */}
            {frameThumbnails.length > 0 && (
              <div className="mb-4">
                <div className="overflow-x-auto">
                  <div className="flex gap-2 pb-2">
                    {frameThumbnails.map((frameUrl, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedFrameIndex(idx)}
                        className={`flex-shrink-0 rounded overflow-hidden transition-all ${
                          selectedFrameIndex === idx
                            ? "ring-2 ring-[#C6D92C] scale-105"
                            : "opacity-70 hover:opacity-100"
                        }`}
                        style={{ width: "80px", height: "60px" }}
                      >
                        <img
                          src={frameUrl}
                          alt={`Frame ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={captureSelectedFrame}
                disabled={frameThumbnails.length === 0}
                className="px-6 py-2 rounded-lg border border-[#C6D92C] text-black font-medium"
                style={{
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                }}
              >
                Use Selected Frame
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowFrameSelector(false);
                  setFrameThumbnails([]);
                  setSelectedFrameIndex(0);
                }}
                className={`px-6 py-2 rounded-lg font-medium ${
                  theme === "dark"
                    ? "bg-[#2C2C2C] text-gray-200"
                    : "bg-gray-200 text-gray-800"
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
          {/* hidden canvas for frame capture */}
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
};

export default ExerciseEdit;
